import type { ArColumnDefinition } from '#types/System.Artifacts.types';
import type { FieldType } from '#types/System.types';
import { formatFieldValueParts } from '@/components/CompositeField/formatPreviewValue';
import type { FormattedCellValue } from '@/components/CompositeField/formattedCellValue';
import { isShippingTrackerFieldTypeId } from '@/components/rendering/fields/ShippingTrackerUI/isShippingTrackerFieldTypeId';
import { formatCellValue } from '@/components/exportTable/exportTableToExcel';

type CompositeFormatter = (
  columnTypeId: string,
  cellValue: unknown
) => string | number | FormattedCellValue | undefined;

export type ShippingOrCompositeCellValue =
  | string
  | number
  | boolean
  | Date
  | null
  | FormattedCellValue;

export function formatShippingOrCompositeCell(params: {
  column: ArColumnDefinition;
  value: unknown;
  fieldTypes?: Record<string, FieldType>;
  formatCompositeValue?: CompositeFormatter;
}): ShippingOrCompositeCellValue {
  const { column, value, fieldTypes, formatCompositeValue } = params;
  const columnId = column.typeId;
  const colFieldType = fieldTypes?.[columnId];

  if (formatCompositeValue) {
    const compositeResult = formatCompositeValue(columnId, value);
    if (compositeResult !== undefined) return compositeResult;
  }

  if (colFieldType && isShippingTrackerFieldTypeId(colFieldType.fieldDataTypeId)) {
    const parts = formatFieldValueParts(value, colFieldType);
    if (typeof parts.formatted === 'object') {
      return {
        display: `${parts.prefix ?? ''}${parts.formatted.display}${parts.suffix ?? ''}`,
        url: parts.formatted.url,
      };
    }
    return `${parts.prefix ?? ''}${parts.formatted}${parts.suffix ?? ''}`;
  }

  return formatCellValue(value, colFieldType) as ShippingOrCompositeCellValue;
}
